import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

const SLIDE_MS = 150;

// Each column's header (by `col:<id>`) and each group header (`group:<id>`)
type Positions = Map<string, number>;

const isReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Every cell of each column (header, body and totals), and each group header
const findElements = (table: HTMLTableElement) => {
  const elements = new Map<string, HTMLElement[]>();
  const add = ({ key, element }: { key: string; element: HTMLElement }) => {
    const list = elements.get(key);
    if (list) {
      list.push(element);
    } else {
      elements.set(key, [element]);
    }
  };
  table
    .querySelectorAll<HTMLElement>('[data-col-id]')
    .forEach((element) =>
      add({ key: `col:${element.dataset.colId}`, element }),
    );
  table
    .querySelectorAll<HTMLElement>('th[data-group-id]')
    .forEach((element) =>
      add({ key: `group:${element.dataset.groupId}`, element }),
    );
  return elements;
};

const readPositions = (table: HTMLTableElement): Positions => {
  const positions: Positions = new Map();
  table
    .querySelectorAll<HTMLElement>('thead th[data-col-id]')
    .forEach((header) =>
      positions.set(
        `col:${header.dataset.colId}`,
        header.getBoundingClientRect().left,
      ),
    );
  table
    .querySelectorAll<HTMLElement>('thead th[data-group-id]')
    .forEach((header) =>
      positions.set(
        `group:${header.dataset.groupId}`,
        header.getBoundingClientRect().left,
      ),
    );
  return positions;
};

// Columns moved while a header is dragged slide across to their new place,
// as AG's do: snapshot() where the headers are just before the order
// changes, and once the new order is drawn each moved column's cells start
// where they were and slide over (a FLIP animation). Not with reduced motion.
const useColumnMoveAnimation = ({
  tableRef,
  orderKey,
}: {
  tableRef: RefObject<HTMLTableElement>;
  // The columns' order: a change after a snapshot slides them
  orderKey: string;
}) => {
  const snapshotRef = useRef<Positions | undefined>();
  const timersRef = useRef<number[]>([]);

  const snapshot = useCallback(() => {
    const table = tableRef.current;
    if (table && !isReducedMotion()) {
      snapshotRef.current = readPositions(table);
    }
  }, [tableRef]);

  useLayoutEffect(() => {
    const before = snapshotRef.current;
    snapshotRef.current = undefined;
    const table = tableRef.current;
    if (!before || !table) {
      return;
    }
    const elements = findElements(table);
    const moved: HTMLElement[] = [];
    readPositions(table).forEach((left, key) => {
      const from = before.get(key);
      if (from === undefined || from === left) {
        return;
      }
      (elements.get(key) ?? []).forEach((element) => {
        element.style.transition = 'none';
        element.style.transform = `translateX(${from - left}px)`;
        moved.push(element);
      });
    });
    if (moved.length === 0) {
      return;
    }
    // Starting from where they were, before sliding over
    table.getBoundingClientRect();
    moved.forEach((element) => {
      element.style.transition = `transform ${SLIDE_MS}ms ease`;
      element.style.transform = '';
    });
    timersRef.current.push(
      window.setTimeout(() => {
        moved.forEach((element) => {
          if (!element.style.transform) {
            element.style.transition = '';
          }
        });
      }, SLIDE_MS + 50),
    );
  }, [orderKey, tableRef]);

  useEffect(
    () => () => {
      timersRef.current.forEach((timer) => window.clearTimeout(timer));
    },
    [],
  );

  return { snapshot };
};

export default useColumnMoveAnimation;
